/**
 * App UI light/dark theme preference. Distinct from the chart-color theme
 * (`uiSlice.ChartTheme`, candle/drawing colors) — this is the whole-app
 * surface theme, applied as `<html data-theme="light|dark">` which flips the
 * design tokens in global.css.
 *
 * Persisted to localStorage (per-device, instant, no flash — the boot script
 * in index.html applies it before first paint). App.tsx mirrors the value to
 * the document; the listener below owns persistence.
 */
import {
  createListenerMiddleware,
  createSlice,
  isAnyOf,
  type PayloadAction,
} from "@reduxjs/toolkit";

export type AppTheme = "dark" | "light";

export const THEME_STORAGE_KEY = "tv-theme";

// LIGHT is the default surface theme — only an explicit stored "dark" opts
// out, so a first visit (or a browser where localStorage throws) lands on
// light. The pre-paint boot script in index.html MUST mirror this test.
function readInitialTheme(): AppTheme {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export interface AppearanceState {
  theme: AppTheme;
}

const initialState: AppearanceState = { theme: readInitialTheme() };

const appearanceSlice = createSlice({
  name: "appearance",
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<AppTheme>) {
      state.theme = action.payload;
    },
    toggleTheme(state) {
      state.theme = state.theme === "dark" ? "light" : "dark";
    },
  },
});

export const { setTheme, toggleTheme } = appearanceSlice.actions;

/**
 * Persist the theme ONLY on an explicit user pick.
 *
 * A previous build persisted from a mount effect in App.tsx, which stamped
 * "dark" into localStorage on every first-ever visit — so a stored "dark"
 * could not be told apart from "never chose one", and changing the default
 * became a no-op for anyone who had loaded the app once. Writing solely from
 * these two actions keeps the stored value meaning "the user picked this".
 */
export const appearanceListener = createListenerMiddleware();

appearanceListener.startListening({
  matcher: isAnyOf(setTheme, toggleTheme),
  effect: (_action, api) => {
    const { theme } = (api.getState() as { appearance: AppearanceState })
      .appearance;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* storage blocked — in-memory only */
    }
  },
});

export default appearanceSlice.reducer;
